// src/pages/BlogStudio.jsx
// Authoring surface for the Community blog (/blog-studio). BLOG-3.
//
// Lives in the signed-in app shell with the global <Navbar />, built with MUI, matching
// Admin.jsx — unlike /community itself, which is a marketing page with its own masthead
// and stylesheet. Not called "blog admin": reviewers and lead reviewers write here too,
// and only admins belong in /admin.
//
// There is no preview screen, on purpose. The public detail endpoint serves an author
// their own unpublished draft at its real URL, so "Preview" opens /community?post=<slug>
// — the actual page a reader will see, not a mock-up of it.
//
// The role check below is a courtesy, not the gate. The real one is @require_role on the
// endpoints; this only avoids showing an editor whose every save would 403.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/api';
import { isBlogAuthor } from '../utils/roles';

// Must agree with VALID_CATEGORIES in backend/routes/blog.py and CATEGORIES in Blog.jsx.
const CATEGORIES = [
  { value: 'writing', label: 'Writing Tips' },
  { value: 'reading', label: 'Reading' },
  { value: 'collaboration', label: 'Collaboration' },
  { value: 'bookknowhow', label: 'Book Know-How' },
  { value: 'publishing', label: 'Publishing' },
];

const EMPTY = { title: '', category: 'writing', tags: '', excerpt: '', body: '' };

export default function BlogStudio() {
  const { user } = useAuth();
  const accessToken = localStorage.getItem('rv_access_token');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  }), [accessToken]);

  const mayAuthor = isBlogAuthor(user);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);   // null = composing a new post
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/blog/mine`, { credentials: 'omit', headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPosts(data.items || []);
    } catch {
      setError("We couldn't load your posts.");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (mayAuthor) load();
    else setLoading(false);
  }, [mayAuthor, load]);

  const startNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setNotice('');
  };

  const startEdit = async (post) => {
    setNotice('');
    setError('');
    // The work-queue list omits the body, so the full post is fetched by slug. An author
    // may read their own draft there, which is the same endpoint the public page uses.
    try {
      const res = await fetch(`${API_BASE}/api/blog/posts/${encodeURIComponent(post.slug)}`, {
        credentials: 'omit', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const full = await res.json();
      setEditingId(full.id);
      setForm({
        title: full.title || '',
        category: full.category || 'writing',
        tags: (full.tags || []).join(', '),
        excerpt: full.excerpt || '',
        body: full.body || '',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError("We couldn't open that post.");
    }
  };

  const payloadFrom = (status) => {
    const body = {
      title: form.title.trim(),
      category: form.category,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      excerpt: form.excerpt.trim(),
      body: form.body,
    };
    if (status) body.status = status;
    return body;
  };

  const save = async (status) => {
    if (!form.title.trim()) {
      setError('A title is required.');
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const url = editingId
        ? `${API_BASE}/api/blog/posts/${editingId}`
        : `${API_BASE}/api/blog/posts`;
      const res = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify(payloadFrom(status)),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setEditingId(data.id);
      setNotice(data.status === 'published' ? 'Published.' : 'Saved as a draft.');
      await load();
    } catch (e) {
      setError(e.message || "We couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (post, status) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/blog/posts/${post.id}`, {
        method: 'PATCH', credentials: 'omit', headers, body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNotice(status === 'published' ? `"${post.title}" is live.` : `"${post.title}" is back to a draft.`);
      await load();
    } catch {
      setError("We couldn't change that post's status.");
    }
  };

  const doDelete = async () => {
    const post = confirmDelete;
    setConfirmDelete(null);
    try {
      const res = await fetch(`${API_BASE}/api/blog/posts/${post.id}`, {
        method: 'DELETE', credentials: 'omit', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (editingId === post.id) startNew();
      setNotice(`"${post.title}" was deleted.`);
      await load();
    } catch {
      setError("We couldn't delete that post.");
    }
  };

  if (!mayAuthor) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert severity="info">
          The blog studio is for ReVisionari staff. If you think you should have access, ask
          an administrator to check your role.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>Blog studio</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Write and publish posts for the Community blog. Everything starts as a draft.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      {/* ---------------- editor ---------------- */}
      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{editingId ? 'Edit post' : 'New post'}</Typography>
          {editingId && <Button size="small" onClick={startNew}>Start a new one</Button>}
        </Stack>

        <Stack spacing={2}>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            fullWidth
            required
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              sx={{ minWidth: 200 }}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Tags"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              helperText="Comma separated, e.g. firstdraft, querying"
              fullWidth
            />
          </Stack>

          <TextField
            label="Excerpt"
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            helperText="Shown on the article card. Leave blank to use the first paragraph."
            fullWidth
          />

          <TextField
            label="Body"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            helperText="Separate paragraphs with a blank line. Reading time is worked out from this."
            multiline
            minRows={12}
            fullWidth
          />
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => save('draft')} disabled={saving}>
            Save draft
          </Button>
          <Button variant="contained" onClick={() => save('published')} disabled={saving}>
            {saving ? 'Saving…' : 'Publish'}
          </Button>
        </Stack>
      </Paper>

      {/* ---------------- work queue ---------------- */}
      <Typography variant="h6" sx={{ mt: 5, mb: 1.5 }}>Your posts</Typography>
      <Divider />

      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}

      {!loading && posts.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          Nothing written yet. The form above is where it starts.
        </Typography>
      )}

      {!loading && posts.map((post) => (
        <Paper key={post.id} variant="outlined" sx={{ p: 2, mt: 1.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 600 }}>{post.title}</Typography>
                <Chip
                  size="small"
                  label={post.status === 'published' ? 'Live' : 'Draft'}
                  color={post.status === 'published' ? 'success' : 'default'}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {post.category} &middot; {post.readTimeMinutes} min read
                {post.authorName ? ` · ${post.authorName}` : ''}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                component={RouterLink}
                to={`/community?post=${encodeURIComponent(post.slug)}`}
                target="_blank"
              >
                Preview
              </Button>
              <Button size="small" onClick={() => startEdit(post)}>Edit</Button>
              {post.status === 'published' ? (
                <Button size="small" onClick={() => setStatus(post, 'draft')}>Unpublish</Button>
              ) : (
                <Button size="small" onClick={() => setStatus(post, 'published')}>Publish</Button>
              )}
              <Button size="small" color="error" onClick={() => setConfirmDelete(post)}>
                Delete
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ))}

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete this post?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &ldquo;{confirmDelete?.title}&rdquo; will be removed for good. If it is live, anyone
            holding a link to it will get a not-found page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Keep it</Button>
          <Button color="error" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
